import { useState, useEffect } from 'react';
import { directus, publicDirectus } from '../lib/directus';
import { readItems, readSingleton } from '@directus/sdk';
import * as Icons from 'lucide-react';
import { Service, Testimonial, FAQItem, ProcessStep, Differential } from '../types';
import { SERVICES as DEFAULT_SERVICES, FAQS as DEFAULT_FAQS, TESTIMONIALS as DEFAULT_TESTIMONIALS, PROCESS_STEPS as DEFAULT_PROCESS_STEPS, CONTACT_INFO as DEFAULT_CONTACT, STATS as DEFAULT_STATS, DIFFERENTIALS as DEFAULT_DIFFERENTIALS } from '../constants';
import { useAuth } from '../context/AuthContext'; // Import useAuth

const getIcon = (name: string): any => {
    // @ts-ignore
    return Icons[name] || Icons.HelpCircle;
};

export const formatWhatsAppUrl = (whatsapp?: string, phoneDisplay?: string): string => {
    const raw = (whatsapp || phoneDisplay || '').trim();
    if (!raw) return DEFAULT_CONTACT.social.whatsapp;
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    const digits = raw.replace(/\D/g, '');
    if (!digits) return DEFAULT_CONTACT.social.whatsapp;
    const withCountry = digits.length <= 11 ? `55${digits}` : digits;
    return `https://wa.me/${withCountry}`;
};

export const formatInstagramUrl = (instagram?: string): string => {
    const raw = (instagram || '').trim();
    if (!raw) return DEFAULT_CONTACT.social.instagram;
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    const handle = raw.startsWith('@') ? raw.slice(1) : raw;
    return `https://www.instagram.com/${handle}`;
};

export const formatLinkedinUrl = (linkedin?: string): string => {
    const raw = (linkedin || '').trim();
    if (!raw) return DEFAULT_CONTACT.social.linkedin;
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    return `https://www.linkedin.com/in/${raw.replace(/^\/+/, '')}`;
};

export const formatPhoneDisplay = (phoneDisplay?: string, whatsapp?: string): string => {
    if (phoneDisplay && phoneDisplay.trim()) return phoneDisplay.trim();
    if (whatsapp) {
        const digits = whatsapp.replace(/\D/g, '');
        const local = digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits;
        if (local.length === 11) {
            return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
        } else if (local.length === 10) {
            return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
        }
    }
    return DEFAULT_CONTACT.whatsapp;
};

export function useSiteData() {
    const { loading: authLoading } = useAuth(); // Wait for Auth Check
    const [services, setServices] = useState<Service[]>(DEFAULT_SERVICES);
    const [testimonials, setTestimonials] = useState<Testimonial[]>(DEFAULT_TESTIMONIALS);
    const [differentials, setDifferentials] = useState<Differential[]>(DEFAULT_DIFFERENTIALS);
    const [faqs, setFaqs] = useState<FAQItem[]>(DEFAULT_FAQS);
    const [processSteps, setProcessSteps] = useState<ProcessStep[]>(DEFAULT_PROCESS_STEPS);
    const [contactInfo, setContactInfo] = useState(DEFAULT_CONTACT);
    const [stats, setStats] = useState(DEFAULT_STATS);
    const [hero, setHero] = useState({
        title: 'Excelência Técnica a\n*Serviço da Verdade*',
        subtitle: '*Perito Ariel Miranda* — Especialista em perícias judiciais e extrajudiciais.',
        cta: 'Solicitar Orçamento'
    });
    const [about, setAbout] = useState({
        title: 'Perito Ariel Miranda',
        subtitle: 'Engenharia, Segurança & Forense Digital',
        text_1: 'No complexo cenário das perícias técnicas...',
        text_2: 'Com atuação nacional...',
        image: '', // will be populated
        badge_title: 'Perito Especialista',
        badge_subtitle: 'Atuação Nacional'
    });

    const [seo, setSeo] = useState({
        title: 'Perito Ariel Miranda | Engenharia Elétrica & Segurança',
        description: 'Perícia Técnica Especializada, Laudos Judiciais e Consultoria em Engenharia Elétrica.',
        keywords: 'perito, engenharia, elétrica, laudos, judicial, segurança do trabalho',
        ogImage: '',
        ogTitle: ''
    });
    const [customization, setCustomization] = useState({
        logo: '',
        favicon: '',
        colors: {
            primary: '',
            accent: ''
        }
    });
    const [popup, setPopup] = useState({
        enabled: false,
        video_file: '',
        title: '',
        subtitle: '',
        cta_text: 'Continuar navegando no site'
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Wait for AuthContext to finish checking token validity
        if (authLoading) return;

        async function fetchData() {
            try {
                const [servicesData, testimonialsData, faqsData, processData, infoData, heroData, differentialsData, aboutData, seoData, popupData] = await Promise.all([
                    publicDirectus.request(readItems('services')).catch(e => { console.error('Error fetching services:', e); return null; }),
                    publicDirectus.request(readItems('testimonials')).catch(e => { console.error('Error fetching testimonials:', e); return null; }),
                    publicDirectus.request(readItems('faqs')).catch(e => { console.error('Error fetching faqs:', e); return null; }),
                    publicDirectus.request(readItems('process_steps')).catch(e => { console.error('Error fetching process_steps:', e); return null; }),
                    publicDirectus.request(readSingleton('Informacoes_Gerais')).catch(e => { console.error('Error fetching Informacoes_Gerais:', e); return null; }),
                    publicDirectus.request(readSingleton('hero_stats')).catch(e => { console.error('Error fetching hero_stats:', e); return null; }),
                    publicDirectus.request(readItems('differentials')).catch(e => { console.error('Error fetching differentials:', e); return null; }),
                    publicDirectus.request(readSingleton('about_section' as any)).catch(e => { console.error('Error fetching about_section:', e); return null; }),
                    publicDirectus.request(readSingleton('seo_config' as any)).catch(e => { console.error('Error fetching seo_config:', e); return null; }),
                    publicDirectus.request(readSingleton('popup_config' as any)).catch(e => { console.error('Error fetching popup_config:', e); return null; })
                ]);

                if (servicesData) {
                    setServices(servicesData.map(s => ({
                        id: String(s.id),
                        slug: s.slug || s.title
                            .toLowerCase()
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '')
                            .replace(/[^a-z0-9]+/g, '-')
                            .replace(/(^-|-$)/g, ''),
                        title: s.title,
                        description: s.description,
                        icon: getIcon(s.icon),
                        features: s.features,
                        details: s.details,
                        hero_image: s.hero_image,
                        allow_attachments: s.allow_attachments
                    })));
                }

                if (testimonialsData) {
                    setTestimonials(testimonialsData.map(t => ({
                        id: String(t.id),
                        name: t.name,
                        role: t.role,
                        content: t.content
                    })));
                }

                if (differentialsData) {
                    setDifferentials(differentialsData.map(d => ({
                        id: String(d.id),
                        title: d.title,
                        description: d.description,
                        icon: getIcon(d.icon)
                    })));
                }

                if (faqsData) {
                    setFaqs(faqsData.map(f => ({
                        id: String(f.id),
                        question: f.question,
                        answer: f.answer
                    })));
                }

                if (processData) {
                    setProcessSteps(processData.map(p => ({
                        id: String(p.id),
                        number: p.number,
                        title: p.title,
                        description: p.description,
                        icon: getIcon(p.icon)
                    })));
                }

                if (infoData) {
                    const info = infoData as any;
                    const phoneDisplay = formatPhoneDisplay(info.phone_display, info.whatsapp);
                    const whatsappUrl = formatWhatsAppUrl(info.whatsapp, info.phone_display);
                    const instagramUrl = formatInstagramUrl(info.instagram);
                    const linkedinUrl = formatLinkedinUrl(info.linkedin);

                    setContactInfo({
                        whatsapp: phoneDisplay,
                        email: info.email || DEFAULT_CONTACT.email,
                        address: info.address || DEFAULT_CONTACT.address,
                        instagram: info.instagram || DEFAULT_CONTACT.instagram,
                        social: {
                            instagram: instagramUrl,
                            linkedin: linkedinUrl,
                            whatsapp: whatsappUrl
                        }
                    });
                }

                if (seoData) {
                    const seo = seoData as any;
                    setSeo({
                        title: seo.site_title || 'Perito Ariel Miranda | Engenharia Elétrica & Segurança',
                        description: seo.site_description || 'Perícia Técnica Especializada em todo o Brasil.',
                        keywords: seo.site_keywords || 'engenharia, pericia, laudos',
                        ogImage: seo.og_image,
                        ogTitle: seo.og_title
                    });
                } else if (infoData) {
                    // Fallback to General Info if SEO config is empty (legacy support)
                    const info = infoData as any;
                    setSeo({
                        title: info.site_title || 'Perito Ariel Miranda | Engenharia Elétrica & Segurança',
                        description: info.site_description || 'Perícia Técnica Especializada em todo o Brasil.',
                        keywords: info.site_keywords || 'engenharia, pericia, laudos',
                        ogImage: info.og_image,
                        ogTitle: info.og_title
                    });

                }

                // Site Customization (Colors & Logo) - Independent of SEO source
                if (infoData) {
                    const info = infoData as any;
                    // Fallback specific for SEO if seoData was missing (handled above inside logic if needed, but here we just do customization)

                    if (info.site_logo || info.primary_color || info.accent_color || info.contact_email || info.site_favicon) {
                        setContactInfo(prev => ({
                            ...prev,
                            recipient_email: info.contact_email || info.email || prev.email,
                        }));

                        setCustomization({
                            logo: info.site_logo,
                            favicon: info.site_favicon,
                            colors: {
                                primary: info.primary_color,
                                accent: info.accent_color
                            }
                        });
                    }
                }

                // ... (existing hero logic) ...


                if (heroData) {
                    const hero = heroData as any;
                    setHero(prev => ({
                        ...prev,
                        title: hero.hero_title || prev.title,
                        subtitle: hero.hero_subtitle || prev.subtitle,
                        cta: hero.cta_text || hero.cta || prev.cta,
                        bg: hero.hero_bg
                    }));

                    setStats([
                        { value: hero.stat_1_value || "12+", label: hero.stat_1_label || "Anos de Experiência" },
                        { value: hero.stat_2_value || "850+", label: hero.stat_2_label || "Laudos Emitidos" },
                        { value: hero.stat_3_value || "100%", label: hero.stat_3_label || "Compromisso Ético" },
                        { value: hero.stat_4_value || "BR", label: hero.stat_4_label || "Atuação Nacional" },
                    ]);
                }

                if (aboutData) {
                    const about = aboutData as any;
                    setAbout({
                        title: about.title || 'Perito Ariel Miranda',
                        subtitle: about.subtitle || 'Engenharia, Segurança & Forense Digital',
                        text_1: about.text_1 || 'Texto padrão...',
                        text_2: about.text_2 || '',
                        image: about.image,
                        badge_title: about.badge_title || 'Perito Especialista',
                        badge_subtitle: about.badge_subtitle || 'Atuação Nacional'
                    });
                }

                if (popupData) {
                    const p = popupData as any;
                    setPopup({
                        enabled: Boolean(p.enabled),
                        video_file: p.video_file || '',
                        title: p.title || '',
                        subtitle: p.subtitle || '',
                        cta_text: p.cta_text || 'Continuar navegando no site'
                    });
                }

            } catch (error) {
                console.error("❌ Failed to fetch CMS data, using defaults:", error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [authLoading]);

    // Debug log to verify data source
    useEffect(() => {
        if (!loading) {
            console.log("📊 Site Data State:", {
                source: services !== DEFAULT_SERVICES ? 'CMS' : 'Constants',
                services,
                differentials
            });
        }
    }, [loading, services, differentials]);

    return { services, testimonials, differentials, faqs, processSteps, contactInfo, stats, hero, seo, about, customization, popup, loading };
}
